import React, { createContext, useContext, useMemo } from "react";

import {
  getChannelComponentConfig,
  generateComponentTag,
  useContentOptimizationContext,
} from "@lib/contentOptimization";
import {
  getDecisioningStudioLiquidAdditionalOptions,
  getDecisioningStudioLiquidExcludedOptions,
} from "@lib/decisioningStudio/decisioningCreativeComponent";
import { exhaustiveCheck } from "@lib/exhaustiveCheck";

import { useFeatureLimitation } from "@services/useFeatureLimitation";
import { useFeatureOn } from "@services/useFeatureOn";

import {
  allEventSpecificOptions,
  baseOptionLists,
  channelSpecificOptionLists,
  eventSpecificOptionLists,
  featureFlipperOptionRequirements,
} from "./data";
import type {
  CustomLiquidSnippet,
  LiquidOption,
  LiquidSettingsContextProviderProps,
  LiquidSettingsContextProviderValue,
  LiquidSettingsSnippetEnricherProps,
} from "./types";

export const defaultLiquidSettings: LiquidSettingsContextProviderValue = {
  allowedLiquidOptions: [] as LiquidOption[],
  dispatchPropertyAllowedInCampaignProperties: true,
  componentOverrides: {},
  alwaysUseLiquidSettingsFromContext: false,
  customAutocompleteSnippets: [],
};

export const LiquidSettingsContext = createContext(defaultLiquidSettings);

export const useLiquidSettingsContext = () => useContext(LiquidSettingsContext);

const useContentOptimizationSnippets = (): CustomLiquidSnippet[] => {
  const contentOptimizationContext = useContentOptimizationContext();
  const featureOn = useFeatureOn();
  const imageVariantsEnabled = featureOn("canvas_content_optimization_image_variants");
  const pushImageVariantsEnabled = featureOn("canvas_content_optimization_image_variants_push");
  const rcsImageVariantsEnabled = featureOn("canvas_content_optimization_image_variants_rcs");

  return useMemo(() => {
    if (!contentOptimizationContext?.isContentOptimization || !contentOptimizationContext?.channel) {
      return [];
    }
    const rcsImageEligible = contentOptimizationContext.rcsImageEligible ?? false;
    const config = getChannelComponentConfig(contentOptimizationContext.channel, {
      imageVariantsEnabled,
      pushImageVariantsEnabled,
      rcsImageVariantsEnabled,
      rcsImageEligible,
    });
    if (!config) {
      return [];
    }
    return config.names.map((componentName) => ({
      label: `message_component '${componentName}'`,
      insertText: generateComponentTag(componentName),
    }));
  }, [contentOptimizationContext, imageVariantsEnabled, pushImageVariantsEnabled, rcsImageVariantsEnabled]);
};

export const LiquidSettingsContextProvider = (props: LiquidSettingsContextProviderProps) => {
  const featureOn = useFeatureOn();
  const { data: connectedContentLimits } = useFeatureLimitation("can_use_connected_content");
  const canUseConnectedContent = !!connectedContentLimits?.limit_value;
  const {
    orchestrationType,
    compositionMessageType,
    children,
    componentOverrides,
    messagesContext,
    additionalOptions,
    excludedOptions,
  } = props;
  const contentOptimizationSnippets = useContentOptimizationSnippets();

  const decisioningStudioLiquidAdditionalOptions = useMemo(
    () => getDecisioningStudioLiquidAdditionalOptions(messagesContext),
    [messagesContext]
  );
  const decisioningStudioLiquidExcludedOptions = useMemo(
    () => getDecisioningStudioLiquidExcludedOptions(messagesContext),
    [messagesContext]
  );
  const mergedAdditionalOptions = useMemo(
    () => [...(additionalOptions || []), ...decisioningStudioLiquidAdditionalOptions],
    [additionalOptions, decisioningStudioLiquidAdditionalOptions]
  );

  const {
    allowedOptions: channelBasedOptions,
    dispatchPropertyAllowedInCampaignProperties,
    alwaysUseLiquidSettingsFromContext,
  } = channelSpecificOptionLists[compositionMessageType];

  const finalSettings = useMemo(() => {
    const baseSettings = baseOptionLists[orchestrationType];

    // apply event specific settings if applicable
    let eventBasedSettings: LiquidOption[];
    switch (orchestrationType) {
      case "campaign":
      case "messageStep":
        eventBasedSettings = props.triggeringEvents.reduce(
          (previous, current) => [...previous, ...eventSpecificOptionLists[current]],
          [] as LiquidOption[]
        );
        break;
      case "all":
      case "anything":
        eventBasedSettings = allEventSpecificOptions;
        break;
      case "landingPage":
        eventBasedSettings = [];
        break;
      default:
        exhaustiveCheck(orchestrationType);
        eventBasedSettings = [];
        break;
    }

    // add all options together
    const optionSet = new Set<LiquidOption>([
      ...baseSettings,
      ...channelBasedOptions,
      ...eventBasedSettings,
      ...mergedAdditionalOptions,
    ]);

    // remove options that don't have their required feature flipper
    optionSet.forEach((option) => {
      const requiredFeature = featureFlipperOptionRequirements[option];

      if (!requiredFeature) {
        return;
      }

      if (!featureOn(requiredFeature)) {
        optionSet.delete(option);
      }
    });

    // Always show event properties for now
    // TODO (COMPI-3015) this should be context dependent (eg only after an action path step)
    optionSet.add("EVENT_PROPERTIES");
    optionSet.add("ADVANCED_EVENT_PROPERTIES");

    // TRANSLATION option should be deleted for non campaign or messageStep orchestrationTypes
    // when the compositionMessageType is not email, banner, iam, or contentBlock
    // Note: This means TRANSLATION is available for Email Templates, Banner Templates, IAM Templates, and Content Blocks
    if (
      !(orchestrationType === "campaign" || orchestrationType === "messageStep") &&
      !(
        compositionMessageType === "email" ||
        compositionMessageType === "banner" ||
        compositionMessageType === "iam" ||
        compositionMessageType === "contentBlock" ||
        (compositionMessageType === "webhook" && featureOn("webhook_multilanguage_support"))
      )
    ) {
      optionSet.delete("TRANSLATION");
    }

    if (compositionMessageType === "webhook" && !featureOn("webhook_multilanguage_support")) {
      optionSet.delete("TRANSLATION");
    }

    // IAM does not support API_TRIGGER_EVENT_PROPERTIES
    if (compositionMessageType === "iam") {
      optionSet.delete("API_TRIGGER_EVENT_PROPERTIES");
    }

    // handle the special case where we don't want event properties in banners
    if (compositionMessageType === "banner") {
      optionSet.delete("EVENT_PROPERTIES");
      optionSet.delete("ADVANCED_EVENT_PROPERTIES");
      optionSet.delete("SMS_PROPERTIES");
      optionSet.delete("WHATS_APP_PROPERTIES");

      // Only remove canvas-specific personalization types for banner campaigns
      // For banner messageStep (canvas) and templates, these should be available
      if (orchestrationType === "campaign") {
        optionSet.delete("API_TRIGGER_EVENT_PROPERTIES");
      }

      if (!featureOn("banners_connected_content_rendering")) {
        optionSet.delete("CONNECTED_CONTENT");
      }
    }

    if (!!connectedContentLimits && channelBasedOptions.includes("CONNECTED_CONTENT") && !canUseConnectedContent) {
      optionSet.delete("CONNECTED_CONTENT");
    }

    (excludedOptions || []).forEach((option) => {
      optionSet.delete(option);
    });

    if (featureOn("decisioning_studio_wizard")) {
      decisioningStudioLiquidExcludedOptions.forEach((option) => {
        optionSet.delete(option);
      });
    }

    return Array.from(optionSet.values());
  }, [
    orchestrationType,
    channelBasedOptions,
    featureOn,
    compositionMessageType,
    connectedContentLimits,
    canUseConnectedContent,
    mergedAdditionalOptions,
    excludedOptions,
    decisioningStudioLiquidExcludedOptions,
    /* @ts-expect-error triggeringEvents only conditionally exists, so TypeScript is unhappy about it being a dep */
    props.triggeringEvents,
  ]);

  const finalValue = useMemo(() => {
    if (
      compositionMessageType === "none" ||
      (orchestrationType === "landingPage" && !featureOn("landing_pages_liquid_personalization"))
    ) {
      // Returning here means that no liquid options are available, and editors like Grapes will not show
      // the liquid personalization option.
      return defaultLiquidSettings;
    }

    return {
      allowedLiquidOptions: finalSettings,
      dispatchPropertyAllowedInCampaignProperties,
      componentOverrides: componentOverrides || {},
      alwaysUseLiquidSettingsFromContext,
      customAutocompleteSnippets: contentOptimizationSnippets,
    };
  }, [
    compositionMessageType,
    orchestrationType,
    finalSettings,
    dispatchPropertyAllowedInCampaignProperties,
    componentOverrides,
    featureOn,
    alwaysUseLiquidSettingsFromContext,
    contentOptimizationSnippets,
  ]);

  return <LiquidSettingsContext.Provider value={finalValue}>{children}</LiquidSettingsContext.Provider>;
};

/**
 * Wraps children with an enriched LiquidSettingsContext that includes additional
 * autocomplete snippets. Reads the existing context value and re-provides it
 * with the extra snippets appended.
 */
export const LiquidSettingsSnippetEnricher = ({ snippets, children }: LiquidSettingsSnippetEnricherProps) => {
  const parentSettings = useLiquidSettingsContext();

  const enrichedSettings = useMemo(
    () => ({
      ...parentSettings,
      customAutocompleteSnippets: [...parentSettings.customAutocompleteSnippets, ...snippets],
    }),
    [parentSettings, snippets]
  );

  return <LiquidSettingsContext.Provider value={enrichedSettings}>{children}</LiquidSettingsContext.Provider>;
};
